import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import {
    getCachedCourses,
    getCachedModules,
    invalidateElearningCache,
    loadElearningCourses,
    loadElearningModules,
} from '../elearningCache';
import Dv2ElearningImage from './Dv2ElearningImage';
import Dv2ElearningMedia from './Dv2ElearningMedia';
import BreakthroughAssessment from './BreakthroughAssessment';
import {
    PROGRAM_CONFIG,
    PROGRAM_IDS,
    calcProgress,
    programHeroPair,
    resolveElearningImagePair,
} from './programConfig';
import './elearningImageV2.css';
import './ELearningHub.css';

const TABS = [
    { id: PROGRAM_IDS.BREAKTHROUGH, label: 'My Breakthrough' },
    { id: PROGRAM_IDS.PATH, label: 'My Path' },
    { id: PROGRAM_IDS.ADDITIONAL, label: 'Additional Programs' },
];

const VALUE_BLURBS = [
    'Build habits that restore energy and daily balance.',
    'Clear space — mentally and physically — for what matters.',
    'Strengthen the beliefs that keep you moving forward.',
    'Grow quiet confidence through intentional practice.',
    'Reconnect with people who fuel your purpose.',
    'Create rhythms that protect your time and focus.',
    'Turn small actions into lasting transformation.',
    'Align your days with the life you are designing.',
    'Deepen awareness so change feels natural, not forced.',
    'Practice presence in the moments that shape you.',
    'Lead yourself with clarity, compassion, and courage.',
    'Celebrate progress and prepare for what comes next.',
];

function pickBlurb(mod, index) {
    const fromApi =
        mod.description ||
        mod.shortDescription ||
        mod.moduleDescription ||
        mod.summary;
    if (fromApi && String(fromApi).trim()) {
        const text = String(fromApi).trim();
        return text.length > 110 ? `${text.slice(0, 107)}…` : text;
    }
    return VALUE_BLURBS[index % VALUE_BLURBS.length];
}

function findContinueModule(modules = []) {
    if (!modules.length) return null;
    const inProgress = modules.find((m) => m.isUnlocked && !m.isCompleted);
    if (inProgress) return inProgress;
    const unlocked = modules.find((m) => m.isUnlocked);
    return unlocked || null;
}

function moduleProgressPct(mod) {
    if (mod.isCompleted) return 100;
    if (mod.isUnlocked) return 50;
    return 0;
}

function resolveModuleImage(mod, cfg) {
    return resolveElearningImagePair({
        moduleId: mod?.id,
        title: mod?.title,
        legacyPath: mod?.imagePath,
        kind: 'module',
        fallbackUrl: cfg?.emptyImage,
    });
}

function resolveCourseImage(course, cfg) {
    return resolveElearningImagePair({
        courseId: course.id,
        title: course.title,
        legacyPath: course.imageUrl,
        fallbackUrl: cfg.emptyImage,
    });
}

function ProgressRing({ percent, size = 88 }) {
    const radius = 36;
    const circ = 2 * Math.PI * radius;
    const offset = circ - (Math.min(100, Math.max(0, percent)) / 100) * circ;

    return (
        <div
            className="dv2-elhub__ritual-ring"
            aria-hidden
            style={{ width: size, height: size }}
        >
            <svg viewBox="0 0 88 88" width={size} height={size}>
                <circle
                    className="dv2-elhub__ritual-track"
                    cx="44"
                    cy="44"
                    r={radius}
                    fill="none"
                    strokeWidth="6"
                />
                <circle
                    className="dv2-elhub__ritual-fill"
                    cx="44"
                    cy="44"
                    r={radius}
                    fill="none"
                    strokeWidth="6"
                    strokeDasharray={circ}
                    strokeDashoffset={offset}
                    transform="rotate(-90 44 44)"
                />
            </svg>
            <div className="dv2-elhub__ritual-pct">
                <strong>{percent}%</strong>
            </div>
        </div>
    );
}

/** Unified progress (left) + continue CTA (right). */
function JourneyBanner({
    completed,
    total,
    percent,
    phaseLabel,
    phaseCopy,
    weekLabel,
    module,
    moduleIndex,
    moduleImage,
    moduleImageFallback,
    moduleImageIsV2,
    programTitle,
    onContinue,
    compact = false,
}) {
    const hasContinue = Boolean(module) && !compact;
    const done = module ? Boolean(module.isCompleted) : false;
    const allDone =
        hasContinue && done && moduleIndex >= total - 1;
    const thumbSrc = moduleImage || moduleImageFallback;

    return (
        <article
            className={`dv2-elhub__journey${
                hasContinue ? ' has-continue' : ''
            }${compact ? ' is-compact' : ''}`}
        >
            <div className="dv2-elhub__journey-progress">
                <ProgressRing percent={percent} size={64} />
                <div className="dv2-elhub__ritual-copy">
                    <p className="dv2-elhub__ritual-stat">
                        <strong>
                            {completed}/{total}
                        </strong>{' '}
                        modules complete
                        {weekLabel ? (
                            <>
                                {' '}
                                · <span>{weekLabel}</span>
                            </>
                        ) : null}
                    </p>
                    <p className="dv2-elhub__ritual-phase">
                        <span className="dv2-elhub__ritual-badge">
                            {phaseLabel}
                        </span>
                        {phaseCopy}
                    </p>
                </div>
            </div>

            {hasContinue ? (
                <div className="dv2-elhub__journey-continue">
                    <div className="dv2-elhub__journey-bridge" aria-hidden />
                    <button
                        type="button"
                        className="dv2-elhub__journey-continue-body"
                        onClick={onContinue}
                    >
                        <div className="dv2-elhub__journey-continue-copy">
                            <p className="dv2-elhub__hero-eyebrow">
                                {allDone
                                    ? 'Journey complete'
                                    : 'Continue where you left off'}
                            </p>
                            <h2 className="dv2-elhub__hero-title">
                                {module.title || `Module ${moduleIndex + 1}`}
                            </h2>
                            <p className="dv2-elhub__hero-meta">
                                Module {moduleIndex + 1}
                            </p>
                        </div>
                        <span className="dv2-elhub__hero-cta">
                            {done ? 'Review' : 'Continue'}
                            <span aria-hidden>→</span>
                        </span>
                    </button>
                    <div
                        className={`dv2-elhub__journey-thumb dv2-elimg-v2${
                            moduleImageIsV2
                                ? ' dv2-elhub__journey-thumb--v2'
                                : ''
                        }`}
                        role="img"
                        aria-label={
                            module.title
                                ? `${module.title} cover`
                                : 'Module cover'
                        }
                    >
                        {thumbSrc ? (
                            <Dv2ElearningImage
                                src={moduleImage || moduleImageFallback}
                                fallbackSrc={moduleImageFallback}
                                alt=""
                                loading="eager"
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}
        </article>
    );
}

function HubCard({
    title,
    description,
    image,
    imageFallback,
    imageIsV2,
    progressLabel,
    progressPct,
    ctaLabel,
    locked,
    completed,
    isCurrent,
    isHighlight,
    unlockHint,
    style,
    onClick,
}) {
    let stateClass = '';
    if (locked) stateClass = ' is-locked';
    else if (isCurrent) stateClass = ' is-current';
    else if (completed) stateClass = ' is-done';

    const canOpen = !locked && typeof onClick === 'function';

    const activate = () => {
        if (canOpen) onClick();
    };

    return (
        <article
            className={`dv2-elhub__card${stateClass}${
                isHighlight ? ' is-highlight' : ''
            }${canOpen ? ' is-clickable' : ''}`}
            style={style}
            data-module-card={isHighlight ? '1' : undefined}
            onClick={canOpen ? activate : undefined}
            onKeyDown={
                canOpen
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate();
                          }
                      }
                    : undefined
            }
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            aria-label={
                canOpen
                    ? `${ctaLabel || 'Open'}: ${title}`
                    : `${title} (locked)`
            }
            aria-disabled={locked || undefined}
        >
            <div
                className={`dv2-elhub__card-media dv2-elimg-v2${
                    imageIsV2 ? ' dv2-elhub__card-media--v2' : ''
                }`}
            >
                {image ? (
                    <Dv2ElearningImage
                        className="dv2-elhub__card-media-img"
                        src={image}
                        fallbackSrc={imageFallback}
                        alt=""
                        loading="lazy"
                    />
                ) : null}
                <div className="dv2-elhub__card-media-fade" aria-hidden />
                {completed ? (
                    <span
                        className="dv2-elhub__card-check"
                        aria-label="Completed"
                    >
                        ✓
                    </span>
                ) : null}
                {locked ? (
                    <div className="dv2-elhub__card-lock" aria-hidden>
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                        >
                            <rect x="5" y="11" width="14" height="10" rx="2" />
                            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                        </svg>
                    </div>
                ) : null}
            </div>
            <div className="dv2-elhub__card-body">
                <div className="dv2-elhub__card-topline">
                    {isCurrent ? (
                        <span className="dv2-elhub__pill dv2-elhub__pill--current">
                            Up next
                        </span>
                    ) : null}
                    {completed && !isCurrent ? (
                        <span className="dv2-elhub__pill dv2-elhub__pill--done">
                            Completed
                        </span>
                    ) : null}
                    {locked ? (
                        <span className="dv2-elhub__pill dv2-elhub__pill--locked">
                            {unlockHint || 'Locked'}
                        </span>
                    ) : null}
                </div>
                <h3 className="dv2-elhub__card-title">{title}</h3>
                {description ? (
                    <p className="dv2-elhub__card-desc">{description}</p>
                ) : null}
                <div className="dv2-elhub__card-progress">
                    <div className="dv2-elhub__card-progress-meta">
                        <span>{progressLabel}</span>
                        <span>{progressPct}%</span>
                    </div>
                    <div className="dv2-elhub__card-bar">
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                </div>
                <span
                    className={`dv2-elhub__card-cta${
                        isCurrent ? ' is-primary' : ''
                    }${completed && !isCurrent ? ' is-ghost' : ''}${
                        locked ? ' is-disabled' : ''
                    }`}
                    aria-hidden
                >
                    {ctaLabel}
                    {!locked ? <span aria-hidden>→</span> : null}
                </span>
            </div>
        </article>
    );
}

function HubListRow({
    title,
    description,
    image,
    imageFallback,
    imageIsV2,
    progressLabel,
    progressPct,
    ctaLabel,
    locked,
    completed,
    isCurrent,
    isHighlight,
    unlockHint,
    style,
    onClick,
}) {
    let stateClass = '';
    if (locked) stateClass = ' is-locked';
    else if (isCurrent) stateClass = ' is-current';
    else if (completed) stateClass = ' is-done';

    const canOpen = !locked && typeof onClick === 'function';

    const activate = () => {
        if (canOpen) onClick();
    };

    return (
        <article
            className={`dv2-elhub__row${stateClass}${
                isHighlight ? ' is-highlight' : ''
            }${canOpen ? ' is-clickable' : ''}`}
            style={style}
            data-module-card={isHighlight ? '1' : undefined}
            onClick={canOpen ? activate : undefined}
            onKeyDown={
                canOpen
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate();
                          }
                      }
                    : undefined
            }
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            aria-label={
                canOpen
                    ? `${ctaLabel || 'Open'}: ${title}`
                    : `${title} (locked)`
            }
            aria-disabled={locked || undefined}
        >
            <div
                className={`dv2-elhub__row-media dv2-elimg-v2${
                    imageIsV2 ? ' dv2-elhub__row-media--v2' : ''
                }`}
            >
                {image ? (
                    <Dv2ElearningImage
                        src={image}
                        fallbackSrc={imageFallback}
                        alt=""
                        loading="lazy"
                    />
                ) : null}
                {completed ? (
                    <span className="dv2-elhub__row-check" aria-label="Completed">
                        ✓
                    </span>
                ) : null}
                {locked ? (
                    <div className="dv2-elhub__row-lock" aria-hidden>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                        >
                            <rect x="5" y="11" width="14" height="10" rx="2" />
                            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                        </svg>
                    </div>
                ) : null}
            </div>
            <div className="dv2-elhub__row-body">
                <div className="dv2-elhub__row-main">
                    <div className="dv2-elhub__card-topline">
                        {isCurrent ? (
                            <span className="dv2-elhub__pill dv2-elhub__pill--current">
                                Up next
                            </span>
                        ) : null}
                        {completed && !isCurrent ? (
                            <span className="dv2-elhub__pill dv2-elhub__pill--done">
                                Completed
                            </span>
                        ) : null}
                        {locked ? (
                            <span className="dv2-elhub__pill dv2-elhub__pill--locked">
                                {unlockHint || 'Locked'}
                            </span>
                        ) : null}
                    </div>
                    <h3 className="dv2-elhub__row-title">{title}</h3>
                    {description ? (
                        <p className="dv2-elhub__row-desc">{description}</p>
                    ) : null}
                </div>
                <div className="dv2-elhub__row-progress">
                    <div className="dv2-elhub__card-progress-meta">
                        <span>{progressLabel}</span>
                        <span>{progressPct}%</span>
                    </div>
                    <div className="dv2-elhub__card-bar">
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                </div>
                <span
                    className={`dv2-elhub__card-cta${
                        isCurrent ? ' is-primary' : ''
                    }${completed && !isCurrent ? ' is-ghost' : ''}${
                        locked ? ' is-disabled' : ''
                    }`}
                    aria-hidden
                >
                    {ctaLabel}
                    {!locked ? <span aria-hidden>→</span> : null}
                </span>
            </div>
        </article>
    );
}

function ModulesViewToggle({ view, onChange }) {
    return (
        <div className="dv2-elhub__view-toggle" role="group" aria-label="Module view">
            <button
                type="button"
                className={`dv2-elhub__view-btn${
                    view === 'cards' ? ' is-active' : ''
                }`}
                onClick={() => onChange('cards')}
                aria-pressed={view === 'cards'}
                title="Card view"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="3" width="7" height="7" rx="1.5" />
                    <rect x="14" y="3" width="7" height="7" rx="1.5" />
                    <rect x="3" y="14" width="7" height="7" rx="1.5" />
                    <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
                <span>Cards</span>
            </button>
            <button
                type="button"
                className={`dv2-elhub__view-btn${
                    view === 'list' ? ' is-active' : ''
                }`}
                onClick={() => onChange('list')}
                aria-pressed={view === 'list'}
                title="List view"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                </svg>
                <span>List</span>
            </button>
        </div>
    );
}

const MODULES_VIEW_KEY = 'dv2_elhub_modules_view';

function ModuleCatalog({
    modules,
    programId,
    cfg,
    continueId,
    highlightId,
    view,
    onOpenModule,
}) {
    const isList = view === 'list';
    const Item = isList ? HubListRow : HubCard;

    return (
        <div className={isList ? 'dv2-elhub__list' : 'dv2-elhub__grid'}>
            {modules.map((mod, index) => {
                const isCurrent = continueId != null && mod.id === continueId;
                const locked = !mod.isUnlocked;
                const completed = Boolean(mod.isCompleted);
                const imagePair = resolveModuleImage(mod, cfg);
                return (
                    <Item
                        key={mod.id}
                        title={mod.title || `Module ${index + 1}`}
                        description={pickBlurb(mod, index)}
                        image={imagePair.src}
                        imageFallback={imagePair.fallbackSrc}
                        imageIsV2={Boolean(imagePair.isV2)}
                        progressLabel={`Module ${index + 1}`}
                        progressPct={moduleProgressPct(mod)}
                        ctaLabel={
                            completed ? 'Review' : locked ? 'Soon' : 'Continue'
                        }
                        locked={locked}
                        completed={completed}
                        isCurrent={isCurrent && !completed}
                        isHighlight={
                            highlightId != null &&
                            String(mod.id) === String(highlightId)
                        }
                        unlockHint={
                            mod.unLockDateText
                                ? `Unlocks ${mod.unLockDateText}`
                                : 'Coming soon'
                        }
                        style={{
                            animationDelay: `${Math.min(index, 11) * 45}ms`,
                        }}
                        onClick={() => onOpenModule(programId, mod)}
                    />
                );
            })}
        </div>
    );
}

/** Premium empty/start hero for My Breakthrough (V2 only). */
function BreakthroughStartHero({ imageSrc, imageFallback, ctaLabel, onCreate }) {
    const heroSrc =
        imageSrc ||
        'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=1200&h=1500&q=80';

    return (
        <article className="dv2-elhub__start">
            <div className="dv2-elhub__start-copy">
                <p className="dv2-elhub__start-eyebrow">My Breakthrough</p>
                <h2 className="dv2-elhub__start-title">
                    Create Your Breakthrough Experience
                </h2>
                <p className="dv2-elhub__start-body">
                    Build a personalized 12-week path based on your life, goals,
                    and Oola journey.
                </p>
                <p className="dv2-elhub__start-support">
                    A new module unlocks every 7 days for 12 weeks.
                </p>
                <ul className="dv2-elhub__start-pills">
                    <li>12-week journey</li>
                    <li>Weekly unlocks</li>
                    <li>Built for you</li>
                </ul>
                <button
                    type="button"
                    className="dv2-elhub__start-cta"
                    onClick={onCreate}
                >
                    {ctaLabel || 'Create Your Custom Program'}
                </button>
            </div>

            <div className="dv2-elhub__start-visual">
                <div className="dv2-elhub__start-media dv2-elimg-v2">
                    <Dv2ElearningImage
                        className="dv2-elimg-v2__img"
                        src={heroSrc}
                        fallbackSrc={imageFallback}
                        alt="Climbing stone steps — the start of your Breakthrough journey"
                    />
                    <div className="dv2-elhub__start-media-shade" aria-hidden />
                </div>
                <div
                    className="dv2-elhub__start-timeline"
                    aria-hidden="true"
                >
                    <span className="dv2-elhub__start-timeline-label">
                        Week 1
                    </span>
                    <div className="dv2-elhub__start-dots">
                        {Array.from({ length: 12 }, (_, i) => (
                            <span
                                key={i}
                                className={`dv2-elhub__start-dot${
                                    i === 0 ? ' is-first' : ''
                                }`}
                            />
                        ))}
                    </div>
                    <span className="dv2-elhub__start-timeline-label">
                        Week 12
                    </span>
                </div>
            </div>
        </article>
    );
}

/** Premium locked/waiting hero for My Path (V2 only). */
function PathLockedHero({
    imageSrc,
    imageFallback,
    unlockedCount = 0,
    completedCount = 0,
    requiredCount = 12,
    onContinue,
}) {
    const heroSrc =
        imageSrc ||
        'https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1200&h=1500&q=80';
    const safeRequired = Math.max(1, requiredCount);
    const progressPct = Math.min(
        100,
        Math.round((unlockedCount / safeRequired) * 100)
    );

    return (
        <article className="dv2-elhub__start dv2-elhub__start--path">
            <div className="dv2-elhub__start-copy">
                <p className="dv2-elhub__start-eyebrow">My Path</p>
                <h2 className="dv2-elhub__start-title">
                    Your deeper journey is waiting
                </h2>
                <p className="dv2-elhub__start-body">
                    Complete your Breakthrough Experience to unlock the next
                    chapter of your Oola journey.
                </p>
                <p className="dv2-elhub__start-support">
                    A new module unlocks every 30 days for 9 months.
                </p>
                <ul className="dv2-elhub__start-pills">
                    <li>9-month journey</li>
                    <li>Monthly unlocks</li>
                    <li>Personalized path</li>
                </ul>

                <div className="dv2-elhub__start-progress">
                    <p className="dv2-elhub__start-progress-label">
                        Breakthrough progress
                    </p>
                    <p className="dv2-elhub__start-progress-stat">
                        <strong>
                            {completedCount} / {requiredCount}
                        </strong>{' '}
                        modules complete
                    </p>
                    <div
                        className="dv2-elhub__start-progress-bar"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={requiredCount}
                        aria-valuenow={unlockedCount}
                        aria-label={`${unlockedCount} of ${requiredCount} Breakthrough modules unlocked`}
                    >
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                    <p className="dv2-elhub__start-progress-meta">
                        {unlockedCount} unlocked
                    </p>
                </div>

                <button
                    type="button"
                    className="dv2-elhub__start-cta"
                    onClick={onContinue}
                >
                    Continue Breakthrough
                </button>
            </div>

            <div className="dv2-elhub__start-visual">
                <div className="dv2-elhub__start-media dv2-elimg-v2">
                    <Dv2ElearningImage
                        className="dv2-elimg-v2__img"
                        src={heroSrc}
                        fallbackSrc={imageFallback}
                        alt="A mountain trail ahead — your Personalized Path waiting beyond Breakthrough"
                    />
                    <div className="dv2-elhub__start-media-shade" aria-hidden />
                </div>
                <div
                    className="dv2-elhub__start-journey"
                    aria-hidden="true"
                >
                    <div className="dv2-elhub__start-journey-phase is-active">
                        <span className="dv2-elhub__start-journey-name">
                            Breakthrough
                        </span>
                        <span className="dv2-elhub__start-journey-span">
                            12 weeks
                        </span>
                    </div>
                    <span className="dv2-elhub__start-journey-arrow">→</span>
                    <div className="dv2-elhub__start-journey-phase is-next">
                        <span className="dv2-elhub__start-journey-name">
                            My Path
                        </span>
                        <span className="dv2-elhub__start-journey-span">
                            9 months
                        </span>
                    </div>
                </div>
            </div>
        </article>
    );
}

/**
 * Top-level E-Learning landing for Dashboard V2.
 */
const ELearningHub = ({
    initialTab = PROGRAM_IDS.BREAKTHROUGH,
    highlightModuleId = null,
    active = true,
    onBack,
    onOpenProgram,
    onOpenModule,
}) => {
    const history = useHistory();
    const location = useLocation();
    const sectionRef = useRef(null);
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession() || {};
    const { token, userId } = session;
    const hasLoadedOnce = useRef(false);
    const cachedMods = userId ? getCachedModules(userId) : null;
    const cachedCourses = userId ? getCachedCourses(userId) : null;

    const [tab, setTab] = useState(initialTab);
    const [assessmentOpen, setAssessmentOpen] = useState(
        () =>
            new URLSearchParams(location.search).get('assessment') === '1'
    );
    const [loading, setLoading] = useState(() => !cachedMods);
    const [coursesLoading, setCoursesLoading] = useState(() => !cachedCourses);
    const [errorMsg, setErrorMsg] = useState('');
    const [breakthroughModules, setBreakthroughModules] = useState(
        () => cachedMods?.myBreakthroughExperience || []
    );
    const [pathModules, setPathModules] = useState(
        () => cachedMods?.myPersonalizedPath || []
    );
    const [courses, setCourses] = useState(() => cachedCourses || []);
    const [modulesView, setModulesView] = useState(() => {
        try {
            const saved = localStorage.getItem(MODULES_VIEW_KEY);
            return saved === 'list' ? 'list' : 'cards';
        } catch (e) {
            return 'cards';
        }
    });

    const setModulesViewAndPersist = (next) => {
        setModulesView(next);
        try {
            localStorage.setItem(MODULES_VIEW_KEY, next);
        } catch (e) {
            /* ignore */
        }
    };

    useEffect(() => {
        setTab(initialTab);
    }, [initialTab]);

    useEffect(() => {
        const open =
            new URLSearchParams(location.search).get('assessment') === '1';
        setAssessmentOpen(open);
    }, [location.search]);

    const applyModules = useCallback((data) => {
        if (!data) return;
        setBreakthroughModules(data.myBreakthroughExperience || []);
        setPathModules(data.myPersonalizedPath || []);
        hasLoadedOnce.current = true;
    }, []);

    const loadModules = useCallback(
        async ({ soft = false, force = false } = {}) => {
            if (!token || !userId) return;
            const hit = getCachedModules(userId);
            if (hit) {
                applyModules(hit);
                setLoading(false);
            } else if (!soft) {
                setLoading(true);
                setErrorMsg('');
            }

            const { error, data } = await loadElearningModules(userId, token, {
                force,
            });
            if (!error && data) {
                applyModules(data);
                setErrorMsg('');
            } else if (!hit && !soft) {
                setErrorMsg('Could not load e-learning programs.');
            }
            setLoading(false);
        },
        [token, userId, applyModules]
    );

    const openBreakthroughAssessment = useCallback(() => {
        setAssessmentOpen(true);
        history.replace(
            `/dashboard-v2?elearning=1&tab=${PROGRAM_IDS.BREAKTHROUGH}&assessment=1`
        );
    }, [history]);

    const closeBreakthroughAssessment = useCallback(() => {
        setAssessmentOpen(false);
        history.replace(
            `/dashboard-v2?elearning=1&tab=${PROGRAM_IDS.BREAKTHROUGH}`
        );
    }, [history]);

    const finishBreakthroughAssessment = useCallback(async () => {
        invalidateElearningCache(userId);
        await loadModules({ force: true });
        setAssessmentOpen(false);
        history.replace(
            `/dashboard-v2?elearning=1&tab=${PROGRAM_IDS.BREAKTHROUGH}`
        );
    }, [userId, loadModules, history]);

    const loadCourses = useCallback(
        async ({ soft = false, force = false } = {}) => {
            if (!token || !userId) return;
            const hit = getCachedCourses(userId);
            if (hit) {
                setCourses(hit);
                setCoursesLoading(false);
            } else if (!soft) {
                setCoursesLoading(true);
            }

            const { error, data } = await loadElearningCourses(userId, token, {
                force,
            });
            if (!error && Array.isArray(data)) {
                setCourses(data);
            }
            setCoursesLoading(false);
        },
        [token, userId]
    );

    useEffect(() => {
        loadModules({ soft: Boolean(getCachedModules(userId)) });
    }, [loadModules, userId]);

    // Courses only when Additional tab needs them (don't block Breakthrough/Path)
    useEffect(() => {
        if (tab !== PROGRAM_IDS.ADDITIONAL) return;
        loadCourses({ soft: Boolean(getCachedCourses(userId)) });
    }, [tab, loadCourses, userId]);

    // When returning to hub from a module, refresh quietly (no Loading flash)
    useEffect(() => {
        if (!active || !hasLoadedOnce.current) return;
        loadModules({ soft: true, force: true });
        if (getCachedCourses(userId)) {
            loadCourses({ soft: true, force: true });
        }
    }, [active, loadModules, loadCourses, userId]);

    useEffect(() => {
        if (!highlightModuleId || loading) return undefined;
        const t = window.setTimeout(() => {
            const el = sectionRef.current?.querySelector(
                '[data-module-card="1"]'
            );
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 280);
        return () => window.clearTimeout(t);
    }, [highlightModuleId, loading, tab]);

    const btProgress = calcProgress(breakthroughModules);
    const pathProgress = calcProgress(pathModules);
    const btCompleted = breakthroughModules.filter((m) => m.isCompleted).length;
    const pathCompleted = pathModules.filter((m) => m.isCompleted).length;
    const unlockedBt = breakthroughModules.filter((m) => m.isUnlocked).length;

    // Same gates as classic MyBreakthrough.jsx
    const userStepId = parseInt(
        session.userStepId || localStorage.getItem('userStepId') || '0',
        10
    );
    const userTypeId = session.userTypeId;
    const shouldShowStepsMessage = userStepId < 4 && userTypeId !== 4;
    const hasBreakthroughAccess =
        (userStepId >= 4 ||
            localStorage.getItem('isActiveCoach') === 'true') &&
        userTypeId !== 4;

    const btContinue = useMemo(
        () => findContinueModule(breakthroughModules),
        [breakthroughModules]
    );
    const pathContinue = useMemo(
        () => findContinueModule(pathModules),
        [pathModules]
    );

    const setTabAndUrl = (nextTab) => {
        setTab(nextTab);
        setAssessmentOpen(false);
        history.replace(`/dashboard-v2?elearning=1&tab=${nextTab}`);
    };

    const weekLabelFor = (completed, total, cadenceDays) => {
        if (!total) return '';
        const week = Math.min(total, Math.max(1, completed + 1));
        if (cadenceDays === 7) return `Week ${week} of ${total}`;
        if (cadenceDays === 30) return `Month ${week} of ${total}`;
        return `Module ${week} of ${total}`;
    };

    let journey = null;
    let content = null;

    if (loading) {
        content = <p className="dv2-elhub__status">Loading your programs…</p>;
    } else if (errorMsg && tab !== PROGRAM_IDS.ADDITIONAL) {
        content = <p className="dv2-elhub__error">{errorMsg}</p>;
    } else if (tab === PROGRAM_IDS.BREAKTHROUGH) {
        const cfg = PROGRAM_CONFIG[PROGRAM_IDS.BREAKTHROUGH];
        // V1: before Design My Life Blueprint → steps CTA
        if (shouldShowStepsMessage) {
            content = (
                <div className="dv2-elhub__empty dv2-elhub__empty--rich">
                    <div className="dv2-elhub__empty-visual" aria-hidden />
                    <h3>Complete Design My Life first</h3>
                    <p>
                        Finish the Design My Life Blueprint experience before
                        you start My Breakthrough.
                    </p>
                    <p className="dv2-elhub__muted">{cfg.phaseCopy}</p>
                    <button
                        type="button"
                        className="dv2-elhub__primary"
                        onClick={() => history.push(cfg.stepsPath || '/steps')}
                    >
                        Go to Design My Life Blueprint
                    </button>
                </div>
            );
        } else if (hasBreakthroughAccess && unlockedBt === 0) {
            const hero = programHeroPair(PROGRAM_IDS.BREAKTHROUGH);
            content = (
                <BreakthroughStartHero
                    imageSrc={hero.src}
                    imageFallback={hero.fallbackSrc}
                    ctaLabel={cfg.quizCta}
                    onCreate={openBreakthroughAssessment}
                />
            );
        } else if (!breakthroughModules.length) {
            const hero = programHeroPair(PROGRAM_IDS.BREAKTHROUGH);
            content = (
                <BreakthroughStartHero
                    imageSrc={hero.src}
                    imageFallback={hero.fallbackSrc}
                    ctaLabel={cfg.quizCta}
                    onCreate={openBreakthroughAssessment}
                />
            );
        } else {
            const cont = btContinue;
            const contIndex = cont
                ? Math.max(
                      0,
                      breakthroughModules.findIndex((m) => m.id === cont.id)
                  )
                : 0;
            const contImage = cont
                ? resolveModuleImage(cont, cfg)
                : programHeroPair(PROGRAM_IDS.BREAKTHROUGH);
            journey = (
                <JourneyBanner
                    completed={btCompleted}
                    total={breakthroughModules.length}
                    percent={btProgress}
                    phaseLabel={cfg.phaseLabel}
                    phaseCopy={cfg.phaseCopy}
                    weekLabel={weekLabelFor(
                        btCompleted,
                        breakthroughModules.length,
                        7
                    )}
                    module={cont}
                    moduleIndex={contIndex}
                    moduleImage={contImage.src}
                    moduleImageFallback={contImage.fallbackSrc}
                    moduleImageIsV2={Boolean(contImage.isV2)}
                    programTitle={cfg.title}
                    onContinue={() =>
                        onOpenModule(PROGRAM_IDS.BREAKTHROUGH, cont)
                    }
                />
            );
            content = (
                <>
                    <div className="dv2-elhub__section-head">
                        <div className="dv2-elhub__section-label">
                            All modules
                        </div>
                        <ModulesViewToggle
                            view={modulesView}
                            onChange={setModulesViewAndPersist}
                        />
                    </div>
                    <ModuleCatalog
                        modules={breakthroughModules}
                        programId={PROGRAM_IDS.BREAKTHROUGH}
                        cfg={cfg}
                        continueId={cont?.id}
                        highlightId={highlightModuleId}
                        view={modulesView}
                        onOpenModule={onOpenModule}
                    />
                </>
            );
        }
    } else if (tab === PROGRAM_IDS.PATH) {
        const cfg = PROGRAM_CONFIG[PROGRAM_IDS.PATH];
        const needed = cfg.requiresBreakthroughUnlocked || 12;
        if (unlockedBt < needed) {
            const hero = programHeroPair(PROGRAM_IDS.PATH);
            content = (
                <PathLockedHero
                    imageSrc={hero.src}
                    imageFallback={hero.fallbackSrc}
                    unlockedCount={unlockedBt}
                    completedCount={btCompleted}
                    requiredCount={needed}
                    onContinue={() => setTabAndUrl(PROGRAM_IDS.BREAKTHROUGH)}
                />
            );
        } else if (!pathModules.length) {
            content = (
                <div className="dv2-elhub__empty dv2-elhub__empty--rich">
                    <div className="dv2-elhub__empty-visual" aria-hidden />
                    <h3>Design your Personalized Path</h3>
                    <p>
                        You unlocked Phase 3. Take the assessment to build the
                        next chapter of your journey.
                    </p>
                    <button
                        type="button"
                        className="dv2-elhub__primary"
                        onClick={() => history.push(cfg.quizPath)}
                    >
                        {cfg.quizCta}
                    </button>
                </div>
            );
        } else {
            const cont = pathContinue;
            const contIndex = cont
                ? Math.max(
                      0,
                      pathModules.findIndex((m) => m.id === cont.id)
                  )
                : 0;
            const contImage = cont
                ? resolveModuleImage(cont, cfg)
                : programHeroPair(PROGRAM_IDS.PATH);
            journey = (
                <JourneyBanner
                    completed={pathCompleted}
                    total={pathModules.length}
                    percent={pathProgress}
                    phaseLabel={cfg.phaseLabel}
                    phaseCopy={cfg.phaseCopy}
                    weekLabel={weekLabelFor(
                        pathCompleted,
                        pathModules.length,
                        30
                    )}
                    module={cont}
                    moduleIndex={contIndex}
                    moduleImage={contImage.src}
                    moduleImageFallback={contImage.fallbackSrc}
                    moduleImageIsV2={Boolean(contImage.isV2)}
                    programTitle={cfg.title}
                    onContinue={() => onOpenModule(PROGRAM_IDS.PATH, cont)}
                />
            );
            content = (
                <>
                    <div className="dv2-elhub__section-head">
                        <div className="dv2-elhub__section-label">
                            All modules
                        </div>
                        <ModulesViewToggle
                            view={modulesView}
                            onChange={setModulesViewAndPersist}
                        />
                    </div>
                    <ModuleCatalog
                        modules={pathModules}
                        programId={PROGRAM_IDS.PATH}
                        cfg={cfg}
                        continueId={cont?.id}
                        highlightId={highlightModuleId}
                        view={modulesView}
                        onOpenModule={onOpenModule}
                    />
                </>
            );
        }
    } else {
        const cfg = PROGRAM_CONFIG[PROGRAM_IDS.ADDITIONAL];
        if (coursesLoading && !courses.length) {
            content = (
                <p className="dv2-elhub__status">Loading your programs…</p>
            );
        } else if (!courses.length) {
            const hero = programHeroPair(PROGRAM_IDS.ADDITIONAL);
            content = (
                <div className="dv2-elhub__empty dv2-elhub__empty--rich">
                    <Dv2ElearningMedia
                        className="dv2-elhub__empty-visual dv2-elhub__empty-visual--add dv2-elimg-v2"
                        src={hero.src}
                        fallbackSrc={hero.fallbackSrc}
                        aria-hidden
                    />
                    <h3>Go deeper with Additional Programs</h3>
                    <p>
                        Certifications and specialty courses appear here once
                        purchased — ready whenever you are.
                    </p>
                    <p className="dv2-elhub__muted">
                        Check back after you add a program to your account.
                    </p>
                </div>
            );
        } else {
            journey = (
                <article className="dv2-elhub__journey is-compact">
                    <div className="dv2-elhub__journey-progress">
                        <div className="dv2-elhub__ritual-copy">
                            <p className="dv2-elhub__ritual-stat">
                                <strong>{courses.length}</strong> purchased
                                program
                                {courses.length === 1 ? '' : 's'}
                            </p>
                            <p className="dv2-elhub__ritual-phase">
                                <span className="dv2-elhub__ritual-badge">
                                    {cfg.phaseLabel}
                                </span>
                                {cfg.phaseCopy}
                            </p>
                        </div>
                    </div>
                </article>
            );
            content = (
                <div className="dv2-elhub__grid">
                    {courses.map((course, index) => {
                        const imagePair = resolveCourseImage(course, cfg);
                        return (
                        <HubCard
                            key={course.id}
                            title={course.title}
                            description="Keep building mastery with your purchased program."
                            image={imagePair.src}
                            imageFallback={imagePair.fallbackSrc}
                            progressLabel="Additional program"
                            progressPct={
                                course.progress != null ? course.progress : 0
                            }
                            ctaLabel="Continue"
                            locked={false}
                            completed={false}
                            isCurrent={index === 0}
                            isHighlight={false}
                            style={{
                                animationDelay: `${Math.min(index, 8) * 45}ms`,
                            }}
                            onClick={() =>
                                onOpenProgram(PROGRAM_IDS.ADDITIONAL, course)
                            }
                        />
                        );
                    })}
                </div>
            );
        }
    }

    return (
        <section
            className={`dv2-elhub dv2-glass dv2-el--strong${
                assessmentOpen ? ' dv2-elhub--assessment' : ''
            }`}
            ref={sectionRef}
        >
            {assessmentOpen ? (
                <BreakthroughAssessment
                    onBack={closeBreakthroughAssessment}
                    onComplete={finishBreakthroughAssessment}
                />
            ) : (
                <>
                    <div className="dv2-elhub__top">
                        <button
                            type="button"
                            className="dv2-elhub__back"
                            onClick={onBack}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>

                    <header className="dv2-elhub__header">
                        <div className="dv2-elhub__header-left">
                            <div className="dv2-elhub__badge-icon" aria-hidden>
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                >
                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                    <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="dv2-elhub__title">
                                    My E-Learning
                                </h1>
                                <p className="dv2-elhub__subtitle">
                                    Build knowledge. Transform your life.
                                </p>
                            </div>
                        </div>
                        <div className="dv2-elhub__level">
                            <span className="dv2-elhub__level-dot" />
                            Level 1
                        </div>
                    </header>

                    <nav
                        className="dv2-elhub__tabs"
                        aria-label="E-Learning programs"
                    >
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`dv2-elhub__tab${
                                    tab === t.id ? ' is-active' : ''
                                }`}
                                onClick={() => setTabAndUrl(t.id)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </nav>

                    {journey}
                    {content}
                </>
            )}
        </section>
    );
};

export default ELearningHub;
